import { Injectable } from '@angular/core';

import { filter, map } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  GraphReponsePaged,
  HandleComplexRequest,
  HandleSimpleRequest,
  graphQlPagedProps,
} from '@camelot/server';
import { Mission, missionProps } from '@camelot/services';
import { isNonNullable } from '@camelot/utils';

import { GET_MISSIONS, GET_MISSIONS_QUERY } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'missionService',
  endpoint: 'mission',
};

@Injectable({
  providedIn: 'root',
})
export class CamMissionsService extends CamBaseService {
  protected _graphEndpoint = graphEndpoint;
  public missions = new HandleSimpleRequest<Mission[]>();
  public mission = new HandleComplexRequest<Mission>();
  public missionsByProject = new HandleComplexRequest<Mission[]>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchForProject$(projectId: string) {
    return this.missionsByProject.fetch(
      projectId,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<Mission>>(
          GET_MISSIONS_QUERY({
            props: graphQlPagedProps(`
              ${missionProps.get('id')}
              ${missionProps.get('title')}
              ${missionProps.get('projectId')}
              ${missionProps.get('type')}
            `),
            where: { projectId: { eq: projectId } },
          }),
          graphEndpoint.clientName
        )
        .pipe(map(data => data.items ?? []))
    );
  }

  public fetchMissions$() {
    return this.missions.fetch(
      this._graphService
        .fetchPagedQueryList<Mission>(
          GET_MISSIONS(
            '',
            `
            ${missionProps.get('id')}
            ${missionProps.get('title')}
            ${missionProps.get('projectId')}
            ${missionProps.get('type')}
          `,
            ``
          ),
          'missions',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items)
        )
    );
  }

  public searchMissions$(search?: string | null, selectedId?: string[] | null) {
    const where = search
      ? `{ title: { contains: "${search ?? ''}" } }`
      : `{ id: { in: [${selectedId?.map(id => `"${id}"`).join(', ')}] } }`;
    return this._graphService
      .fetchPagedQueryList<Mission>(
        GET_MISSIONS(
          `where: ${where}`,
          `
            ${missionProps.get('id')}
            ${missionProps.get('title')}
            ${missionProps.get('projectId')}
            ${missionProps.get('type')}
          `,
          ``
        ),
        'missions',
        graphEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        map(data => data.items)
      );
  }

  public fetchMission$(id: string) {
    return this.mission.fetch(
      id,
      this._graphService
        .fetchPagedQueryList<Mission>(
          GET_MISSIONS(
            `where: { id: { eq: "${id}" } }`,
            `
                ${missionProps.get('id')}
                ${missionProps.get('title')}
                ${missionProps.get('projectId')}
                ${missionProps.get('type')}
              `,
            ''
          ),
          'missions',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => (data.items ?? [])[0] ?? null)
        )
    );
  }
}
